import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { Modal } from '@material-ui/core'
import TextField from '@mui/material/TextField'
import { Button } from '@resiliatech/birdsong'
import { EntryInput } from 'Generated/graphql-types'
import {
  AddChartDataModalContainer,
  Buttons,
  Header,
  Inputs,
} from './AddChartDataModal.styled'
import { DatePicker } from '../../../../SharedComponents/DatePicker/DatePicker'
import { DataPointMap } from '../types'

type AddChartDataModalProps = {
  categories: { goalToDimensionId: number; label: string }[]
  isOpen: boolean
  onAddData: (date: string, values: EntryInput[]) => void
  onClose: () => void
}

const DATE_FORMAT = 'YYYY-MM-DD'

export const AddChartDataModal = ({
  categories,
  isOpen,
  onAddData,
  onClose,
}: AddChartDataModalProps) => {
  const today = moment(new Date()).format(DATE_FORMAT)
  const [date, setDate] = useState<string | null>(today)
  const [dataPointMap, setValues] = useState<DataPointMap>({})

  // Determine what dimensions we are updating
  useEffect(() => {
    const dimensionMap = categories?.reduce(
      (acc, { goalToDimensionId, label }) => ({
        ...acc,
        [goalToDimensionId]: {
          goalToDimensionId,
          label,
          value: '',
        },
      }),
      {}
    )
    setValues(dimensionMap)
  }, [categories])

  const handleUpdateDimension = (dimensionToUpdate: number, value: number) => {
    const existingValue = dataPointMap[dimensionToUpdate]
    const newValue = { ...existingValue, value }
    setValues({ ...dataPointMap, [dimensionToUpdate]: newValue })
  }

  const handleClose = () => {
    setDate(today)
    setValues({})
    onClose()
  }

  if (!Object.entries(dataPointMap).length) return null

  const handleSave = (dateToAdd: string | null, valuesToAdd: DataPointMap) => {
    const entriesToAdd = Object.values(valuesToAdd).map(
      ({ goalToDimensionId, value }) => ({
        goalToDimensionId,
        value,
      })
    )
    if (dateToAdd) {
      onAddData(dateToAdd, entriesToAdd)
      setValues({})
      setDate(today)
    }
  }

  const inputsByDimension = categories.map((dimension) => (
    <TextField
      key={dimension.label}
      id={`editChartModalInput${dimension.label}`}
      label={dimension.label}
      variant='outlined'
      size='small'
      type='number'
      value={dataPointMap[dimension.goalToDimensionId].value}
      onChange={({ target: { value } }) =>
        handleUpdateDimension(dimension.goalToDimensionId, parseInt(value, 10))
      }
      required
    />
  ))

  // We have a date of entry and values for every data point category
  const hasValidValues =
    !!Object.values(dataPointMap).length &&
    Object.values(dataPointMap).every((value) => value) &&
    !!date

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      disableEnforceFocus
    >
      <AddChartDataModalContainer>
        {/* eslint-disable-next-line react/no-unescaped-entities */}
        <Header>Add Data Point</Header>
        <Inputs>
          <DatePicker
            date={date}
            label='Date'
            onDateSelected={(newDate) =>
              setDate(newDate ? moment(newDate).format(DATE_FORMAT) : null)
            }
          />
          {inputsByDimension}
        </Inputs>
        <Buttons>
          <Button
            label='Cancel'
            onClick={handleClose}
            type='button'
            variant='tertiary'
          />
          <Button
            label='Save'
            onClick={() => handleSave(date, dataPointMap)}
            type='button'
            variant='primary'
            isDisabled={!hasValidValues}
          />
        </Buttons>
      </AddChartDataModalContainer>
    </Modal>
  )
}
